import { CELLAR, SAVE, START, TIME, type SupplyId, type TaskId, TASK_IDS } from '../config/constants';
import type { Batch } from '../process/Batch';
import type { StaffLocation, StaffNavTarget, StaffNavStep } from './StaffNavigation';
import type { InteractiveAttempt } from './TaskAttempt';
import { normalizeAttempts } from './TaskAttempt';
import { ROOMS } from '../world/rooms';
import { tilePoint, recoverPosition, pointTile, type Point } from '../world/Movement';
import { COIN_UPGRADES, type CoinUpgradeId } from '../data/coinUpgrades';

export type RoomId = 'barn' | 'creamery' | 'cellar' | 'field';
export type FeedType = 'basic' | 'quality';

export interface InventoryMilk {
  kind: 'milk';
  qty: number; // gallons
  quality: number; // 1.0 .. 5.0
  cream: number; // percent
  madeMinute: number; // absolute game minute
  ownerId?: string;
  ownerName?: string;
}
export interface InventoryCheese {
  kind: 'cheese';
  cheeseId: string;
  qty: number;
  stars: number;
  madeDay: number;
  /** Aging value multiplier baked in when a wheel leaves the cellar */
  agedMult?: number;
  ownerId?: string;
  ownerName?: string;
}
export interface InventoryWhey {
  kind: 'whey';
  qty: number; // gallons
  /** Quality of the batch it drained from (weighted when stacks merge) */
  quality: number;
}
export type InventoryItem = InventoryMilk | InventoryCheese | InventoryWhey;

/** A stack of wheels on the cellar racks */
export interface AgingStack {
  id: string;
  cheeseId: string;
  qty: number;
  stars: number;
  /** Effective aging days (environment speed applied) */
  ageDays: number;
  rackedDay: number;
  lastWashAge: number;
  missedWashes: number;
  stalledDays: number;
  ownerId?: string;
  ownerName?: string;
}

export interface CodexState {
  discovered: string[];
  learned: string[];
  /** Question indices answered correctly at least once, per cheese */
  correct: Record<string, number[]>;
  attempts: Record<string, number>;
}

export interface LearningMilestones {
  madeCheese: boolean;
  agedCheese: boolean;
  soldCheese: boolean;
}

export type StaffStation = 'barn' | 'vat' | 'cellar';

export interface EmployeeStats {
  intelligence: number;
  workEthic: number;
  loyalty: number;
  hygiene: number;
}

export interface Employee {
  id: string;
  name: string;
  stats: EmployeeStats;
  hiredXp: Record<TaskId, number>;
  trainedXp: Record<TaskId, number>;
  wage: number;
  trainerFor: TaskId | null;
  station: StaffStation | null;
  hiredDay: number;
  presentToday: boolean;
  daysWorked: number;
  daysMissed: number;
  jobStatus?: 'idle' | 'walking' | 'working' | 'carrying' | 'waiting';
  jobProgress?: number;
  jobLabel?: string;
  jobPosition?: { x: number; y: number };
  blockedReason?: string | null;
  position?: Point & { room: RoomId };
  continuousPath?: Point[];
  location?: StaffLocation;
  navTarget?: StaffNavTarget;
  navPath?: StaffLocation[];
  navStep?: StaffNavStep;
  navigationBlocked?: string | null;
  job?: StaffJob;
  /** Apply reassignment only after the current product has been delivered. */
  pendingStation?: StaffStation | null;
  leaving?: boolean;
}

export interface StaffJob {
  coinUpgradeSnapshot?: Partial<Record<CoinUpgradeId, number>>;
  kind: 'milk' | 'cheese' | 'cellar' | 'retrieval';
  phase: 'work' | 'deliver';
  step: number;
  elapsed: number;
  duration: number;
  recipeId?: string;
  batch?: Batch;
  products: InventoryItem[];
  /** Reserved raw inputs and supplies have already left shared inventory. */
  input?: InventoryMilk;
}

export interface StaffState {
  employees: Employee[];
  candidates: Employee[];
  candidatesDay: number;
  /** Recipe selected for new creamery jobs; in-flight jobs keep their recipe. */
  vatRecipe: string | null;
  nextId: number;
}

export interface SaveState {
  version: string;
  saveTimestamp: number;
  day: number;
  minuteOfDay: number;
  energy: number;
  gold: number;
  gems: number;
  xp: { total: number; tasks: Record<TaskId, number>; bank: Record<TaskId, number> };
  supplies: Record<SupplyId, number>;
  inventory: InventoryItem[];
  bessie: { location: 'barn' | 'pasture'; fedToday: boolean; feedType: FeedType | null; lastMilkedMinute: number | null; milkingsToday: number };
  vat: { durability: number; batch: Batch | null };
  table: { batch: Batch | null };
  tub: { batch: Batch | null };
  cellar: { temp: number; humidity: number; racks: AgingStack[]; nextId: number };
  codex: CodexState;
  staff: StaffState;
  market: { weekIndex: number; soldThisWeek: Record<string, number>; snapshotDate: string; saleRemainder?: number };
  skills: string[];
  coinUpgradeLevels: Record<CoinUpgradeId, number>;
  pinnedUpgradeId?: CoinUpgradeId | null;
  interactiveAttempts: Record<string, InteractiveAttempt>;
  rewardHistory: string[];
  room: RoomId;
  playerPosition: Point;
  playerTile: { x: number; y: number };
  playerMovingTo?: { x: number; y: number } | null;
  stats: { batches: number; unitsSold: number; goldEarned: number; gemsFound: number; daysPlayed: number; wagesPaid: number };
  tutorial: { done: boolean; completed: TaskId[] };
  learning: LearningMilestones;
  settings: { relaxedTiming: boolean; devMode: boolean };
}

export function zeroTasks(): Record<TaskId, number> {
  return Object.fromEntries(TASK_IDS.map((t) => [t, 0])) as Record<TaskId, number>;
}

export function createNewState(): SaveState {
  return {
    version: SAVE.VERSION,
    saveTimestamp: Date.now(),
    day: 1,
    minuteOfDay: TIME.DAY_START,
    energy: START.ENERGY,
    gold: START.GOLD,
    gems: START.GEMS,
    xp: { total: 0, tasks: zeroTasks(), bank: zeroTasks() },
    supplies: { ...START.SUPPLIES },
    inventory: [],
    bessie: { location: 'barn', fedToday: false, feedType: null, lastMilkedMinute: null, milkingsToday: 0 },
    vat: { durability: 100, batch: null },
    table: { batch: null },
    tub: { batch: null },
    cellar: { temp: CELLAR.START_TEMP, humidity: CELLAR.START_HUM, racks: [], nextId: 1 },
    codex: { discovered: ['mozzarella_fresh'], learned: ['mozzarella_fresh'], correct: {}, attempts: {} },
    staff: { employees: [], candidates: [], candidatesDay: 0, vatRecipe: 'mozzarella_fresh', nextId: 1 },
    market: { weekIndex: 0, soldThisWeek: {}, snapshotDate: '' },
    skills: ['barn', 'creamery'],
    coinUpgradeLevels: Object.fromEntries(COIN_UPGRADES.map(u => [u.id, 0])) as Record<CoinUpgradeId, number>,
    pinnedUpgradeId: null,
    interactiveAttempts: {},
    rewardHistory: [],
    room: 'barn',
    playerPosition: tilePoint({ x: 4, y: 4 }),
    playerTile: { x: 4, y: 4 },
    stats: { batches: 0, unitsSold: 0, goldEarned: 0, gemsFound: 0, daysPlayed: 0, wagesPaid: 0 },
    tutorial: { done: false, completed: [] },
    learning: { madeCheese: false, agedCheese: false, soldCheese: false },
    settings: { relaxedTiming: false, devMode: false },
  };
}

export function absMinute(s: SaveState): number {
  return (s.day - 1) * 1440 + s.minuteOfDay;
}

export function formatClock(minuteOfDay: number): string {
  const m = Math.floor(minuteOfDay) % 1440;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/** Browser storage, or null when running headless (Node simulation). */
function storage(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' && localStorage ? localStorage : null;
  } catch {
    return null;
  }
}

export function loadState(): SaveState | null {
  try {
    const raw = storage()?.getItem(SAVE.KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SaveState>;
    if (parsed.version !== SAVE.VERSION) {
      try { storage()?.setItem(`${SAVE.KEY}-pre-migration`, raw); } catch { /* backup is best effort */ }
    }
    return migrate(parsed);
  } catch (err) {
    console.warn('Save could not be read, starting fresh', err);
    return null;
  }
}

export function saveState(s: SaveState): void {
  try {
    s.saveTimestamp = Date.now();
    storage()?.setItem(SAVE.KEY, JSON.stringify(s));
  } catch (err) {
    console.warn('Save failed', err);
  }
}

export function clearSave(): void {
  try {
    storage()?.removeItem(SAVE.KEY);
  } catch {
    /* ignore */
  }
}

export function exportSave(s: SaveState): string {
  return btoa(unescape(encodeURIComponent(JSON.stringify(s))));
}

export function importSave(text: string): SaveState | null {
  try {
    const json = decodeURIComponent(escape(atob(text.trim())));
    return migrate(JSON.parse(json) as Partial<SaveState>);
  } catch {
    return null;
  }
}

/** Fill any missing fields from a fresh state so older saves keep working. */
export function migrate(partial: Partial<SaveState>): SaveState {
  // Never mutate the source save while applying one-time mass conversions.
  partial = structuredClone(partial);
  const fresh = createNewState();
  const merged: SaveState = {
    ...fresh,
    ...partial,
    xp: {
      total: partial.xp?.total ?? 0,
      tasks: { ...fresh.xp.tasks, ...(partial.xp?.tasks ?? {}) },
      bank: { ...fresh.xp.bank, ...(partial.xp?.bank ?? {}) },
    },
    supplies: { ...fresh.supplies, ...(partial.supplies ?? {}) },
    bessie: { ...fresh.bessie, ...(partial.bessie ?? {}), location: partial.skills?.includes('field') && partial.bessie?.location !== 'barn' ? 'pasture' : 'barn' },
    vat: { ...fresh.vat, ...(partial.vat ?? {}) },
    table: { ...fresh.table, ...(partial.table ?? {}) },
    tub: { ...fresh.tub, ...(partial.tub ?? {}) },
    cellar: { ...fresh.cellar, ...(partial.cellar ?? {}) },
    codex: { ...fresh.codex, ...(partial.codex ?? {}) },
    staff: { ...fresh.staff, ...(partial.staff ?? {}) },
    market: { ...fresh.market, ...(partial.market ?? {}) },
    stats: { ...fresh.stats, ...(partial.stats ?? {}) },
    tutorial: { ...fresh.tutorial, ...(partial.tutorial ?? {}) },
    learning: { ...fresh.learning, ...(partial.learning ?? {}) },
    settings: { ...fresh.settings, ...(partial.settings ?? {}) },
    coinUpgradeLevels: { ...fresh.coinUpgradeLevels, ...(partial.coinUpgradeLevels ?? {}) },
    interactiveAttempts: normalizeAttempts(partial.interactiveAttempts),
    rewardHistory: Array.isArray(partial.rewardHistory) ? partial.rewardHistory.filter(x => typeof x === 'string') : [],
    playerTile: { ...fresh.playerTile, ...(partial.playerTile ?? {}) },
  };
  for (const u of COIN_UPGRADES) {
    const n = merged.coinUpgradeLevels[u.id];
    merged.coinUpgradeLevels[u.id] = Number.isFinite(n) ? Math.max(0, Math.min(15, Math.floor(n))) : 0;
  }
  // Preserve mass from older playtests when converting recipe-specific packages
  // to common one-pound portions. Once versioned, this never runs again.
  if (partial.version && /^2\.[01]\./.test(partial.version)) {
    const legacyPounds: Record<string, number> = { mozzarella_fresh: 1, ricotta: 1, taleggio: 2, gouda: 4, havarti: 3, cheddar: 5, emmental: 8, parmesan: 10, pecorino_romano: 8, roquefort: 6, gorgonzola: 6 };
    for (const item of merged.inventory) if (item.kind === 'cheese') item.qty *= legacyPounds[item.cheeseId] ?? 1;
    for (const rack of merged.cellar.racks) rack.qty *= legacyPounds[rack.cheeseId] ?? 1;
    for (const slot of [merged.vat, merged.table, merged.tub]) if (slot.batch?.process === 'taleggio') slot.batch.units *= 2;
  }
  // Saves from 2.0 stored batches without a process and whey without a quality.
  for (const slot of [merged.vat, merged.table, merged.tub]) {
    const b = slot.batch as (Batch & { process?: string }) | null;
    if (b && !b.process) b.process = 'mozzarella';
    if (b && b.moldTurns === undefined) b.moldTurns = [];
    if (b && b.ownerId === undefined) b.ownerId = 'player';
    if (b && b.ownerName === undefined) b.ownerName = 'Player';
    if (b && b.matrixActive === undefined) b.matrixActive = false;
    if (b && b.perfectedTasks === undefined) b.perfectedTasks = [];
    if (b && b.perfectionWindows === undefined) b.perfectionWindows = {};
    if (b) { b.interactiveAttempts = normalizeAttempts(b.interactiveAttempts); b.coinUpgradeSnapshot ??= { ...merged.coinUpgradeLevels }; }
  }
  for (const item of merged.inventory) {
    if (item.kind === 'whey' && typeof item.quality !== 'number') item.quality = 3;
  }
  if (!merged.codex.discovered.includes('mozzarella_fresh')) merged.codex.discovered.push('mozzarella_fresh');
  if (!merged.codex.learned.includes('mozzarella_fresh')) merged.codex.learned.push('mozzarella_fresh');
  for (const e of merged.staff.employees.concat(merged.staff.candidates)) {
    e.hiredXp = { ...zeroTasks(), ...e.hiredXp };
    e.trainedXp = { ...zeroTasks(), ...e.trainedXp };
    e.jobStatus ??= 'idle';
    e.jobProgress ??= 0;
    e.jobLabel ??= '';
    e.blockedReason ??= null;
    // Old progress had no reserved inputs or carried output. Restart that legacy
    // timer, while preserving every 1D job and its navigation verbatim.
    if (!e.job) { e.jobProgress = 0; e.jobStatus = 'idle'; }
  }
  if (merged.pinnedUpgradeId && !COIN_UPGRADES.some(u => u.id === merged.pinnedUpgradeId)) merged.pinnedUpgradeId = null;
  if (!ROOMS[merged.room]) merged.room = 'barn';
  merged.playerPosition = recoverPosition(ROOMS[merged.room], merged.skills, partial.playerPosition ?? tilePoint(merged.playerTile));
  merged.playerTile = pointTile(merged.playerPosition);
  merged.playerMovingTo = null;
  for (const e of merged.staff.employees) {
    const room = e.position?.room && ROOMS[e.position.room] ? e.position.room : e.location?.room && ROOMS[e.location.room] ? e.location.room : e.station === 'vat' ? 'creamery' : e.station === 'cellar' ? 'cellar' : 'barn';
    let position = e.position ?? tilePoint(e.location ?? ROOMS[room].spawn);
    if (!e.position && e.navStep && e.navStep.from.room === e.navStep.to.room) {
      const a=tilePoint(e.navStep.from), b=tilePoint(e.navStep.to), t=Math.max(0,Math.min(1,e.navStep.progress));
      position={x:a.x+(b.x-a.x)*t,y:a.y+(b.y-a.y)*t};
    }
    e.position = {room,...recoverPosition(ROOMS[room],merged.skills,position)};
    e.location = {room,...pointTile(e.position)};
    e.navStep=undefined;e.navPath=undefined;e.continuousPath=undefined;
  }
  merged.version = SAVE.VERSION;
  return merged;
}
